// This controller's purpose is to handle addresses suggestions on adress forms.
// Refer to https://gorails.com/episodes/google-maps-places-autocomplete-with-rails for usage tutorial
// Google's documentation: https://developers.google.com/maps/documentation/javascript/places-autocomplete#style_autocomplete

import { Controller } from "@hotwired/stimulus"
import { Loader } from '@googlemaps/js-api-loader';
import { observeGoogleMapsTheme, readGoogleMapsColorScheme } from "../utils/google_maps_theme"

export default class extends Controller {
  static targets = [ "field", "latitude", "longitude", "zipcode", "city", "country", "map" ]
  static values = ["latitude", "longitude", "key"]

  async connect() {
    if (!this.hasMapTarget) return;

    const loader = new Loader({
        apiKey: this.data.get("key"),
        version: "weekly",
        libraries: ["places"]
    });

    await loader.load();
    this.initMap();
    this.unobserveTheme = observeGoogleMapsTheme((colorScheme) => this.applyColorScheme(colorScheme))
  }

  disconnect() {
    this.unobserveTheme?.()
  }

  applyColorScheme(colorScheme) {
    this.map?.setOptions({ colorScheme })
  }

  initMap() {
    const colorScheme = readGoogleMapsColorScheme()

    if (this.hasMapTarget) {
      this.map = new google.maps.Map(this.mapTarget, {
        center: new google.maps.LatLng(this.data.get("latitude") || 46.71, this.data.get("longitude") || 1.71),
        zoom: (this.data.get("latitude") == null ? 6 : 15),
        colorScheme,
      })

      this.marker = new google.maps.Marker({
        map: this.map,
        anchorPoint: new google.maps.Point(0, -29)
      })
    }

    const options = {
      componentRestrictions: { country: "fr" },
      fields: ["address_components", "geometry", "icon", "name"],
      types: ["address"],
    };
    this.autocomplete = new google.maps.places.Autocomplete(this.fieldTarget, options);
    if (this.hasMapTarget) {
      this.autocomplete.bindTo('bounds', this.map)
    }
    this.autocomplete.setFields(['address_components', 'geometry', 'icon', 'name'])
    this.autocomplete.addListener('place_changed', this.placeChanged.bind(this))
  }

  placeChanged() {
    let place = this.autocomplete.getPlace()

    if (!place.geometry) {
      window.alert(`No details available for input: ${place.name}`)
      return
    }

    if (this.hasMapTarget) {
      if (place.geometry.viewport) {
        this.map.fitBounds(place.geometry.viewport)
      } else {
        this.map.setCenter(place.geometry.location)
        this.map.setZoom(15)
      }

      this.marker.setPosition(place.geometry.location)
      this.marker.setVisible(true)
    }

    this.latitudeTarget.value = place.geometry.location.lat()
    this.longitudeTarget.value = place.geometry.location.lng()
    this.fieldTarget.value = place.name

    for (let component of place.address_components) {
      if (component.types[0] == "postal_code") {
        this.zipcodeTarget.value = component.long_name;
        this.zipcodeTarget.dispatchEvent(new Event("input", { bubbles: true }));
      }
      if (component.types[0] == "locality") {
        this.cityTarget.value = component.long_name;
        this.cityTarget.dispatchEvent(new Event("input", { bubbles: true }));
      }
      if (component.types[0] == "country") {
        const countryName = component.long_name;
        if (this.hasCountryTarget) {
          const selectElement = this.countryTarget;
          if (selectElement.type === 'select-one') {
            // Handle dropdown
            const options = Array.from(selectElement.options);
            const matchingOption = options.find(option => option.text.includes(countryName));
            if (matchingOption) {
              selectElement.value = matchingOption.value;
            }
          } else {
            // Handle hidden field
            selectElement.value = countryName;
          }
          this.countryTarget.dispatchEvent(new Event("input", { bubbles: true }));
        }
      }
    }
  }

  keydown(event) {
    if (event.key == "Enter") {
      event.preventDefault()
    }
  }
}
